/**
 * Common language validation utilities for CMS content
 * Used by both client and server code for consistent language handling
 */

import { LANGUAGES, UI_TO_CMS_MAP, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, SupportedLanguage } from './cmsLanguageConstants';

/**
 * Validates and normalizes a language parameter
 * @param {string | null | undefined} language - Language code to validate
 * @returns {SupportedLanguage} Valid language code or default language
 */
export const validateLanguage = (language: string | null | undefined): SupportedLanguage => {
    if (!language || typeof language !== 'string') {
        return DEFAULT_LANGUAGE;
    }

    const normalized = language.toLowerCase();
    
    if (!SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)) {
        return DEFAULT_LANGUAGE;
    }

    return normalized as SupportedLanguage;
};

/**
 * Maps UI or browser language codes to CMS languages
 * @param {string | null | undefined} userLanguage - User's preferred language
 * @param {string | null | undefined} browserLanguage - Browser detected language
 * @returns {SupportedLanguage} Language to use for CMS requests
 */
export const getLanguageForCMS = (
    userLanguage?: string | null, 
    browserLanguage?: string | null
): SupportedLanguage => {
    // Try user preference first
    if (userLanguage && typeof userLanguage === 'string') {
        const normalized = userLanguage.toLowerCase();
        
        // Direct CMS language match
        if (SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)) {
            return normalized as SupportedLanguage;
        }
        
        // Map UI language to CMS language
        const cmsLanguage = UI_TO_CMS_MAP[normalized as keyof typeof UI_TO_CMS_MAP];
        if (cmsLanguage) {
            return cmsLanguage;
        }
        
        // Generic language prefix mapping
        if (normalized.startsWith('es')) return LANGUAGES.SPANISH;
        if (normalized.startsWith('en')) return LANGUAGES.ENGLISH;
    }

    // Browser language fallback
    if (browserLanguage?.toLowerCase().startsWith('es')) {
        return LANGUAGES.SPANISH;
    }

    return DEFAULT_LANGUAGE;
};